import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Store as StoreIcon,
  Users,
  Monitor,
  Search,
  Plus,
  MapPin,
  Wifi,
  WifiOff,
  CheckCircle,
  XCircle,
  Trash2,
  ExternalLink,
} from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import {
  listStores,
  listMerchantUsers,
  listPosDevices,
  toggleUserActive,
  togglePosAvailable,
  deletePos,
  type Store,
  type MerchantUser,
  type PosDevice,
} from "@/lib/api";
import { formatCashierName, cashierInitials } from "@/lib/cashierName";
import { formatRelative } from "@/lib/format";

type TabType = "stores" | "cashiers" | "terminals";

export function OperationsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<TabType>("stores");

  const storesQ = useQuery<Store[]>({ queryKey: ["stores"], queryFn: listStores });
  const usersQ = useQuery<MerchantUser[]>({ queryKey: ["users"], queryFn: listMerchantUsers });
  const posQ = useQuery<PosDevice[]>({ queryKey: ["pos-devices"], queryFn: listPosDevices });

  const stores = storesQ.data ?? [];
  const cashiers = (usersQ.data ?? []).filter((u) => u.role === "cashier");
  const devices = posQ.data ?? [];

  const activeCashiers = cashiers.filter((u) => u.is_active).length;
  const activeDevices = devices.filter((p) => p.is_available).length;

  return (
    <div>
      <PageHeader
        title="Mon commerce"
        breadcrumbs={[{ label: "Mon commerce" }, { label: "Vue opérationnelle" }]}
        subtitle="Vos magasins, caissiers et terminaux POS en un seul endroit."
      />

      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <SummaryCard
          icon={<StoreIcon size={18} />}
          label="Magasins"
          value={stores.length}
          sub={`${stores.filter((s) => s.is_active).length} actifs`}
          color="primary"
        />
        <SummaryCard
          icon={<Users size={18} />}
          label="Caissiers"
          value={cashiers.length}
          sub={`${activeCashiers} actifs`}
          color="success"
        />
        <SummaryCard
          icon={<Monitor size={18} />}
          label="Terminaux POS"
          value={devices.length}
          sub={`${activeDevices} disponibles`}
          color="info"
        />
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-slate-100">
          <div className="flex" role="tablist">
            <TabButton
              active={tab === "stores"}
              onClick={() => setTab("stores")}
              icon={<StoreIcon size={14} />}
              count={stores.length}
              label="Magasins"
            />
            <TabButton
              active={tab === "cashiers"}
              onClick={() => setTab("cashiers")}
              icon={<Users size={14} />}
              count={cashiers.length}
              label="Caissiers"
            />
            <TabButton
              active={tab === "terminals"}
              onClick={() => setTab("terminals")}
              icon={<Monitor size={14} />}
              count={devices.length}
              label="Terminaux POS"
            />
          </div>
        </div>

        {tab === "stores" && <StoresTab items={stores} loading={storesQ.isLoading} />}
        {tab === "cashiers" && (
          <CashiersTab
            items={cashiers}
            loading={usersQ.isLoading}
            onToggle={async (id, active) => {
              await toggleUserActive(id, !active);
              qc.invalidateQueries({ queryKey: ["users"] });
            }}
          />
        )}
        {tab === "terminals" && (
          <TerminalsTab
            items={devices}
            loading={posQ.isLoading}
            onToggle={async (posId, avail) => {
              await togglePosAvailable(posId, !avail);
              qc.invalidateQueries({ queryKey: ["pos-devices"] });
            }}
            onDelete={async (posId) => {
              if (!confirm("Supprimer définitivement ce terminal POS ?")) return;
              await deletePos(posId);
              qc.invalidateQueries({ queryKey: ["pos-devices"] });
            }}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------

function TabButton({
  active,
  onClick,
  icon,
  count,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  count: number;
  label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${
        active
          ? "border-brand-500 text-brand-700"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {icon}
      {label}
      <span
        className={`text-[10px] px-1.5 py-0.5 rounded-md ${
          active ? "bg-brand-50 text-brand-700" : "bg-slate-100 text-slate-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}

function SearchBar({
  value,
  onChange,
  placeholder,
  createLabel,
  onCreate,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  createLabel?: string;
  onCreate?: () => void;
}) {
  return (
    <div className="px-4 py-3 border-b border-slate-100 flex flex-col sm:flex-row gap-2 sm:items-center">
      <div className="relative flex-1">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="form-input pl-9 py-2 text-sm w-full"
        />
      </div>
      {createLabel && (
        <button
          type="button"
          onClick={onCreate}
          disabled={!onCreate}
          className="btn btn-primary btn-sm shrink-0"
          title={onCreate ? undefined : "Création à venir dans une prochaine version"}
        >
          <Plus size={14} />
          {createLabel}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function StoresTab({ items, loading }: { items: Store[]; loading: boolean }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return items;
    return items.filter(
      (x) =>
        x.name.toLowerCase().includes(s) ||
        (x.location || "").toLowerCase().includes(s) ||
        (x.store_email || "").toLowerCase().includes(s),
    );
  }, [items, q]);

  return (
    <>
      <SearchBar
        value={q}
        onChange={setQ}
        placeholder="Rechercher un magasin par nom, lieu ou email…"
        createLabel="Nouveau magasin"
      />
      {loading ? (
        <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">Aucun magasin trouvé.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {filtered.map((s) => (
            <div key={s.id} className="px-4 py-3 flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center shrink-0">
                <StoreIcon size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold text-[#181c32]">{s.name}</div>
                  {s.is_active ? (
                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-success-50 text-success-700">
                      Actif
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                      Inactif
                    </span>
                  )}
                  {s.has_pos_device && (
                    <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-info-50 text-info-700">
                      POS
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                  <MapPin size={11} className="text-slate-400" />
                  {s.location || "—"}
                  {s.store_type && s.store_type !== "physical" && (
                    <span className="italic text-slate-400">· {s.store_type}</span>
                  )}
                </div>
                {(s.store_email || s.store_phone) && (
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                    {s.store_email && <span>{s.store_email}</span>}
                    {s.store_phone && <span className="font-mono">{s.store_phone}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function CashiersTab({
  items,
  loading,
  onToggle,
}: {
  items: MerchantUser[];
  loading: boolean;
  onToggle: (id: number, currentActive: boolean) => Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return items;
    return items.filter(
      (u) =>
        formatCashierName(u).toLowerCase().includes(s) ||
        u.firstname.toLowerCase().includes(s) ||
        u.lastname.toLowerCase().includes(s) ||
        u.email.toLowerCase().includes(s) ||
        u.phone_number.includes(q),
    );
  }, [items, q]);

  const toggle = async (u: MerchantUser) => {
    setBusyId(u.id);
    try {
      await onToggle(u.id, u.is_active);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <SearchBar
        value={q}
        onChange={setQ}
        placeholder="Rechercher un caissier par nom, email ou téléphone…"
        createLabel="Nouveau caissier"
      />
      {loading ? (
        <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">Aucun caissier trouvé.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {filtered.map((u) => {
            const isBusy = busyId === u.id;
            return (
              <div key={u.id} className="px-4 py-3 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-500 text-white font-bold flex items-center justify-center shrink-0">
                  {cashierInitials(u)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-[#181c32]">{formatCashierName(u)}</div>
                    {u.is_active ? (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-success-50 text-success-700">
                        Actif
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                        Désactivé
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 truncate">
                    <span className="font-mono">{u.username}</span>
                    {u.phone_number && u.phone_number !== "00000000000000" && (
                      <span> · {u.phone_number}</span>
                    )}
                    {u.email && <span> · {u.email}</span>}
                  </div>
                  {u.last_login_at && (
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      Dernier login : {formatRelative(u.last_login_at)}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  disabled={isBusy}
                  onClick={() => toggle(u)}
                  className={`btn btn-sm ${
                    u.is_active ? "btn-light" : "btn-primary"
                  } shrink-0`}
                  title={u.is_active ? "Désactiver le compte" : "Réactiver le compte"}
                >
                  {u.is_active ? <XCircle size={13} /> : <CheckCircle size={13} />}
                  {isBusy ? "…" : u.is_active ? "Désactiver" : "Activer"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function TerminalsTab({
  items,
  loading,
  onToggle,
  onDelete,
}: {
  items: PosDevice[];
  loading: boolean;
  onToggle: (posId: string, currentAvailable: boolean) => Promise<void>;
  onDelete: (posId: string) => Promise<void>;
}) {
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return items;
    return items.filter(
      (p) =>
        p.pos_id.toLowerCase().includes(s) ||
        (p.store_name || "").toLowerCase().includes(s) ||
        (p.location || "").toLowerCase().includes(s),
    );
  }, [items, q]);

  const doToggle = async (p: PosDevice) => {
    setBusyId(p.pos_id);
    try {
      await onToggle(p.pos_id, p.is_available);
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async (p: PosDevice) => {
    setBusyId(p.pos_id);
    try {
      await onDelete(p.pos_id);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <SearchBar
        value={q}
        onChange={setQ}
        placeholder="Rechercher un terminal (ID, magasin, lieu)…"
        createLabel="Nouveau terminal"
      />
      {loading ? (
        <div className="p-6 text-center text-sm text-slate-500">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">Aucun terminal trouvé.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {filtered.map((p) => {
            const isBusy = busyId === p.pos_id;
            return (
              <div key={p.pos_id} className="px-4 py-3 flex items-center gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    p.is_available
                      ? "bg-success-50 text-success-600"
                      : "bg-slate-100 text-slate-400"
                  }`}
                >
                  {p.is_available ? <Wifi size={16} /> : <WifiOff size={16} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-mono text-sm font-semibold text-[#181c32] truncate">
                      {p.pos_id}
                    </div>
                    {p.is_available ? (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-success-50 text-success-700">
                        Disponible
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                        Indisponible
                      </span>
                    )}
                    {p.is_super_agent && (
                      <span className="text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-info-50 text-info-700">
                        Super agent
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                    <StoreIcon size={11} className="text-slate-400" />
                    {p.store_name || "—"}
                    {p.location && <span className="text-slate-400">· {p.location}</span>}
                  </div>
                  {p.callback_url && (
                    <div className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1 truncate">
                      <ExternalLink size={9} />
                      <span className="truncate">{p.callback_url}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => doToggle(p)}
                    className={`btn btn-sm ${
                      p.is_available ? "btn-light" : "btn-primary"
                    }`}
                    title={
                      p.is_available
                        ? "Marquer indisponible"
                        : "Remettre en service"
                    }
                  >
                    {p.is_available ? <WifiOff size={13} /> : <Wifi size={13} />}
                    {isBusy ? "…" : p.is_available ? "Suspendre" : "Activer"}
                  </button>
                  <button
                    type="button"
                    disabled={isBusy}
                    onClick={() => doDelete(p)}
                    className="btn btn-sm text-danger-600 hover:bg-danger-50"
                    title="Supprimer le terminal"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------

function SummaryCard({
  icon,
  label,
  value,
  sub,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub?: string;
  color: "primary" | "success" | "info";
}) {
  const cls: Record<typeof color, string> = {
    primary: "bg-brand-50 text-brand-700",
    success: "bg-success-50 text-success-700",
    info: "bg-info-50 text-info-700",
  };
  return (
    <div className="card card-body py-3">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${cls[color]}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-[#7e8299] font-bold">
            {label}
          </div>
          <div className="text-lg font-bold text-[#181c32] leading-tight">
            {value.toLocaleString("fr-FR")}
          </div>
          {sub && <div className="text-[11px] text-slate-500 truncate">{sub}</div>}
        </div>
      </div>
    </div>
  );
}
